import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useBrandManager } from './useBrandManager';

const defaultItems = [
  { id: "home", label: "Home", icon: "Home", action: "navigate", path: "/dashboard", order: 0, visible: true, showBadge: false },
  { id: "reports", label: "Reports", icon: "ChartPie", action: "navigate", path: "/reporting-dashboard", order: 1, visible: true, showBadge: false },
  { id: "activity", label: "Activity", icon: "Activity", action: "navigate", path: "/my-overall-activity", order: 2, visible: true, showBadge: false },
  { id: "more", label: "More", icon: "MoreHorizontal", action: "toggle-sidebar", order: 3, visible: true, showBadge: false }
];

type SettingsSource = 'user' | 'brand' | 'default';

export const useMobileFooterNavSettingsWithBrand = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentBrandId } = useBrandManager();

  const { data, isLoading } = useQuery({
    queryKey: ['mobile-footer-nav-settings', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return null;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Try to get user override first
      const { data: userSettings } = await supabase
        .from('os_mobile_footer_nav_settings')
        .select('*')
        .eq('brand_id', currentBrandId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (userSettings) {
        return {
          settings: userSettings,
          source: 'user' as SettingsSource,
          hasUserOverride: true
        };
      }

      // Fall back to brand defaults
      const { data: brandSettings } = await supabase
        .from('os_mobile_footer_nav_settings')
        .select('*')
        .eq('brand_id', currentBrandId)
        .is('user_id', null)
        .maybeSingle();

      if (brandSettings) {
        return {
          settings: brandSettings,
          source: 'brand' as SettingsSource,
          hasUserOverride: false
        };
      }

      // Fall back to system defaults
      return {
        settings: {
          enabled: true,
          items: defaultItems
        },
        source: 'default' as SettingsSource,
        hasUserOverride: false
      };
    },
    enabled: !!currentBrandId,
  });

  const updateUserSettings = useMutation({
    mutationFn: async (newSettings: { enabled?: boolean; items?: any[] }) => {
      if (!currentBrandId) throw new Error('No brand selected');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Prefer explicit update-then-insert to avoid ON CONFLICT issues
      const { data: updateData, error: updateError } = await supabase
        .from('os_mobile_footer_nav_settings')
        .update({
          ...newSettings,
          updated_at: new Date().toISOString(),
        })
        .eq('brand_id', currentBrandId)
        .eq('user_id', user.id)
        .select();

      if (updateError) throw updateError;

      // If nothing was updated, insert a new row
      if (!updateData || updateData.length === 0) {
        const { error: insertError } = await supabase
          .from('os_mobile_footer_nav_settings')
          .insert({
            brand_id: currentBrandId,
            user_id: user.id,
            enabled: newSettings.enabled ?? true,
            items: newSettings.items ?? defaultItems,
          });
        if (insertError) throw insertError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mobile-footer-nav-settings'] });
      toast({
        title: "Personal settings updated",
        description: "Your mobile footer navigation has been customized.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
      console.error('Error updating user settings:', error);
    },
  });

  const clearUserOverride = useMutation({
    mutationFn: async () => {
      if (!currentBrandId) throw new Error('No brand selected');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('os_mobile_footer_nav_settings')
        .delete()
        .eq('brand_id', currentBrandId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mobile-footer-nav-settings'] });
      toast({
        title: "Settings reset",
        description: "Reverted to brand default mobile footer navigation.",
      });
    },
  });

  return {
    settings: data?.settings || null,
    source: data?.source || 'default',
    hasUserOverride: data?.hasUserOverride || false,
    isLoading,
    updateUserSettings: updateUserSettings.mutate,
    clearUserOverride: clearUserOverride.mutate,
  };
};
